// GLB files (binary glTF 2.0): reading, describing and changing the models
// Blip has made (models.js, docs/BLIP_MODELS_DESIGN.md). Nothing here needs a
// page, so Node checks it too (web/ide/tests/glb_check.mjs), and the server
// makes shared assets' files with it (library.mjs, docs/SHARED_ASSETS_DESIGN.md).
// It lives with the server, whose deploy takes only web/server; the IDE
// imports it through web/ide/src/blip/glb.js.
//
// A GLB is a header, a JSON chunk (the glTF document) and a BIN chunk (the
// buffer its accessors read). readGlb gives { json, bin }; writeGlb makes
// the file again.

const MAGIC = 0x46546c67, JSON_CHUNK = 0x4e4f534a, BIN_CHUNK = 0x004e4942;

export class GlbError extends Error {}

export function isGlb(bytes) {
  return bytes.byteLength >= 12 && new DataView(bytes.buffer, bytes.byteOffset, 12).getUint32(0, true) === MAGIC;
}

export function readGlb(bytes) {
  // A plain Uint8Array, whose slices are copies (a Node Buffer's are not).
  const data = ArrayBuffer.isView(bytes) ? new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength) : new Uint8Array(bytes);
  if (!isGlb(data)) throw new GlbError("It is not a GLB file");
  const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
  if (view.getUint32(4, true) !== 2) throw new GlbError("It is not a glTF 2.0 file");
  const length = Math.min(view.getUint32(8, true), data.byteLength);
  let json = null, bin = new Uint8Array(0);
  for (let offset = 12; offset + 8 <= length;) {
    const size = view.getUint32(offset, true), type = view.getUint32(offset + 4, true);
    const chunk = data.subarray(offset + 8, Math.min(length, offset + 8 + size));
    if (type === JSON_CHUNK && !json) json = JSON.parse(new TextDecoder().decode(chunk));
    else if (type === BIN_CHUNK) bin = chunk;
    offset += 8 + size;
  }
  if (!json) throw new GlbError("The GLB file has no glTF document");
  return { json, bin };
}

export function writeGlb({ json, bin }) {
  if (bin.byteLength) {
    json.buffers = json.buffers && json.buffers.length ? json.buffers : [{}];
    json.buffers[0].byteLength = bin.byteLength;
  }
  const text = new TextEncoder().encode(JSON.stringify(json));
  const textSize = text.byteLength + (4 - text.byteLength % 4) % 4;
  const binSize = bin.byteLength + (4 - bin.byteLength % 4) % 4;
  const out = new Uint8Array(12 + 8 + textSize + (bin.byteLength ? 8 + binSize : 0));
  const view = new DataView(out.buffer);
  view.setUint32(0, MAGIC, true);
  view.setUint32(4, 2, true);
  view.setUint32(8, out.byteLength, true);
  view.setUint32(12, textSize, true);
  view.setUint32(16, JSON_CHUNK, true);
  out.set(text, 20);
  out.fill(0x20, 20 + text.byteLength, 20 + textSize);
  if (bin.byteLength) {
    view.setUint32(20 + textSize, binSize, true);
    view.setUint32(24 + textSize, BIN_CHUNK, true);
    out.set(bin, 28 + textSize);
  }
  return out;
}

// ------------------------------------------------------------ describing

const COMPONENTS = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4, MAT2: 4, MAT3: 9, MAT4: 16 };

// A node's matrix from its matrix or its translation, rotation and scale,
// column by column, as glTF keeps them.
function nodeMatrix(node) {
  if (Array.isArray(node.matrix) && node.matrix.length === 16) return node.matrix;
  const [tx, ty, tz] = node.translation || [0, 0, 0];
  const [x, y, z, w] = node.rotation || [0, 0, 0, 1];
  const [sx, sy, sz] = node.scale || [1, 1, 1];
  return [
    (1 - 2 * (y * y + z * z)) * sx, 2 * (x * y + z * w) * sx, 2 * (x * z - y * w) * sx, 0,
    2 * (x * y - z * w) * sy, (1 - 2 * (x * x + z * z)) * sy, 2 * (y * z + x * w) * sy, 0,
    2 * (x * z + y * w) * sz, 2 * (y * z - x * w) * sz, (1 - 2 * (x * x + y * y)) * sz, 0,
    tx, ty, tz, 1,
  ];
}

function multiply(a, b) {
  const out = new Array(16).fill(0);
  for (let column = 0; column < 4; ++column) {
    for (let row = 0; row < 4; ++row) {
      for (let k = 0; k < 4; ++k) out[column * 4 + row] += a[k * 4 + row] * b[column * 4 + k];
    }
  }
  return out;
}

const IDENTITY = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

// Each node's matrix in the scene, by its index.
function worldMatrices(json) {
  const nodes = json.nodes || [];
  const parent = new Array(nodes.length).fill(-1);
  nodes.forEach((node, index) => (node.children || []).forEach((child) => { parent[child] = index; }));
  const world = new Array(nodes.length);
  const of = (index, depth = 0) => {
    if (world[index]) return world[index];
    const local = nodeMatrix(nodes[index]);
    world[index] = parent[index] < 0 || depth > 200 ? local : multiply(of(parent[index], depth + 1), local);
    return world[index];
  };
  nodes.forEach((_, index) => of(index));
  return world;
}

// Where a skin puts its mesh at rest: its first bone's matrix in the scene
// times that bone's inverse bind matrix (identity when the file has none).
function skinMatrix(json, bin, skin, world) {
  if (!skin || !skin.joints || !skin.joints.length || !world[skin.joints[0]]) return IDENTITY;
  let inverse = IDENTITY;
  const accessor = skin.inverseBindMatrices !== undefined && (json.accessors || [])[skin.inverseBindMatrices];
  const view = accessor && (json.bufferViews || [])[accessor.bufferView];
  if (view && accessor.componentType === 5126) {
    const at = (view.byteOffset || 0) + (accessor.byteOffset || 0);
    if (at + 64 <= bin.byteLength) {
      const data = new DataView(bin.buffer, bin.byteOffset + at, 64);
      inverse = Array.from({ length: 16 }, (_, index) => data.getFloat32(index * 4, true));
    }
  }
  return multiply(world[skin.joints[0]], inverse);
}

// A picture's width and height from its first bytes: PNG, JPEG or WebP.
export function pictureSize(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (bytes.byteLength > 24 && bytes[0] === 0x89 && bytes[1] === 0x50) return { width: view.getUint32(16), height: view.getUint32(20) };
  if (bytes.byteLength > 4 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    for (let at = 2; at + 9 < bytes.byteLength;) {
      if (bytes[at] !== 0xff) return null;
      const marker = bytes[at + 1], size = view.getUint16(at + 2);
      if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
        return { width: view.getUint16(at + 7), height: view.getUint16(at + 5) };
      }
      at += 2 + size;
    }
    return null;
  }
  if (bytes.byteLength > 30 && String.fromCharCode(...bytes.subarray(8, 12)) === "WEBP") {
    const kind = String.fromCharCode(...bytes.subarray(12, 16));
    if (kind === "VP8 ") return { width: view.getUint16(26, true) & 0x3fff, height: view.getUint16(28, true) & 0x3fff };
    if (kind === "VP8L") {
      const bits = view.getUint32(21, true);
      return { width: (bits & 0x3fff) + 1, height: ((bits >> 14) & 0x3fff) + 1 };
    }
    if (kind === "VP8X") {
      const three = (at) => bytes[at] | bytes[at + 1] << 8 | bytes[at + 2] << 16;
      return { width: three(24) + 1, height: three(27) + 1 };
    }
  }
  return null;
}

// What a model holds: { triangles, nodes, meshes, materials, textures,
// pictures: [{ type, bytes, width, height }], bounds: { min, max } (in the
// scene, at rest), bones, boneNames (some), mixamo, clips: [{ name,
// seconds }], extensionsRequired, generator, origin (where Blip had it
// made) }.
export function describeGlb({ json, bin }) {
  const accessors = json.accessors || [], views = json.bufferViews || [];
  const meshes = json.meshes || [], nodes = json.nodes || [];
  const world = worldMatrices(json);
  let triangles = 0;
  const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
  nodes.forEach((node, index) => {
    if (node.mesh === undefined || !meshes[node.mesh]) return;
    // A skinned mesh is placed by its bones: at rest, each bone's matrix in
    // the scene times its inverse bind matrix, the first bone's standing for
    // them all.
    const matrix = node.skin !== undefined ? skinMatrix(json, bin, json.skins[node.skin], world) : world[index];
    for (const primitive of meshes[node.mesh].primitives || []) {
      const mode = primitive.mode ?? 4;
      const position = accessors[(primitive.attributes || {}).POSITION];
      const count = primitive.indices !== undefined && accessors[primitive.indices] ? accessors[primitive.indices].count :
        position ? position.count : 0;
      triangles += mode === 4 ? Math.floor(count / 3) : mode === 5 || mode === 6 ? Math.max(0, count - 2) : 0;
      if (!position || !Array.isArray(position.min) || !Array.isArray(position.max)) continue;
      for (let corner = 0; corner < 8; ++corner) {
        const point = [0, 1, 2].map((axis) => (corner >> axis) & 1 ? position.max[axis] : position.min[axis]);
        for (let axis = 0; axis < 3; ++axis) {
          const value = matrix[axis] * point[0] + matrix[4 + axis] * point[1] + matrix[8 + axis] * point[2] + matrix[12 + axis];
          min[axis] = Math.min(min[axis], value);
          max[axis] = Math.max(max[axis], value);
        }
      }
    }
  });
  const pictures = (json.images || []).map((image) => {
    const view = views[image.bufferView];
    if (!view) return { type: image.mimeType || "", bytes: 0, width: 0, height: 0 };
    const bytes = bin.subarray(view.byteOffset || 0, (view.byteOffset || 0) + view.byteLength);
    const size = pictureSize(bytes) || { width: 0, height: 0 };
    return { type: image.mimeType || "", bytes: view.byteLength, ...size };
  });
  const joints = new Set();
  for (const skin of json.skins || []) for (const joint of skin.joints || []) joints.add(joint);
  const boneNames = [...joints].map((index) => nodes[index] && nodes[index].name).filter(Boolean);
  const clips = (json.animations || []).map((animation, index) => {
    let seconds = 0;
    for (const sampler of animation.samplers || []) {
      const input = accessors[sampler.input];
      if (input && Array.isArray(input.max)) seconds = Math.max(seconds, input.max[0]);
    }
    return { name: animation.name || `Clip ${index + 1}`, seconds };
  });
  const asset = json.asset || {};
  return {
    triangles, nodes: nodes.length, meshes: meshes.length, materials: (json.materials || []).length,
    textures: (json.textures || []).length, pictures,
    bounds: Number.isFinite(min[0]) ? { min, max } : null,
    bones: joints.size, boneNames: boneNames.slice(0, 80), mixamo: boneNames.some((name) => /^mixamorig/i.test(name)),
    clips, extensionsRequired: json.extensionsRequired || [], generator: asset.generator || "",
    origin: asset.extras && typeof asset.extras.blip === "object" ? asset.extras.blip : null,
  };
}

// -------------------------------------------------------------- changing

// Names the clips, in order, when there are as many names as clips.
// Returns whether it did.
export function nameClips({ json }, names) {
  const animations = json.animations || [];
  if (!animations.length || animations.length !== names.length) return false;
  animations.forEach((animation, index) => { animation.name = names[index]; });
  return true;
}

// Stands a model of Tripo's as glTF models stand: its front, which Tripo
// turns to +x, to +z (so that in Blitz3D+, which turns z round, it faces the
// camera, as other glTF models do), its feet at y 0 and its middle at x 0, z
// 0. Its roots go under one new root node, which does both, so that its
// skeleton, clips and parts are as they were. Returns whether it moved.
export function standGlb(glb) {
  const { json } = glb;
  const info = describeGlb(glb);
  if (!info.bounds || !json.scenes || !json.scenes.length) return false;
  // One stood already, such as one that gained more clips, stays as it is.
  const roots = json.scenes[json.scene || 0].nodes || [];
  if (roots.length === 1 && json.nodes[roots[0]] && json.nodes[roots[0]].name === "Stand") return false;
  const { min, max } = info.bounds;
  const cx = (min[0] + max[0]) / 2, cz = (min[2] + max[2]) / 2;
  // A quarter turn about y, clockwise seen from above: +x goes to +z, and a
  // point (x, y, z) to (-z, y, x).
  const node = { name: "Stand", rotation: [0, -Math.SQRT1_2, 0, Math.SQRT1_2], translation: [cz, -min[1], -cx] };
  json.nodes ||= [];
  const scene = json.scenes[json.scene || 0];
  node.children = scene.nodes || [];
  json.nodes.push(node);
  scene.nodes = [json.nodes.length - 1];
  return true;
}

// Makes a model's pictures (its textures) at most size pixels across:
// resize(bytes, type, size) gives { bytes, type } for each larger one, or
// null to keep it. Its buffer is made again around the new pictures.
// Returns how many it made smaller.
export async function shrinkTextures(glb, size, resize) {
  const { json, bin } = glb;
  const views = json.bufferViews || [];
  const changed = new Map();      // a picture's buffer view -> its new bytes
  for (const image of json.images || []) {
    const view = views[image.bufferView];
    if (!view || (view.buffer || 0) !== 0 || changed.has(image.bufferView)) continue;
    const bytes = bin.subarray(view.byteOffset || 0, (view.byteOffset || 0) + view.byteLength);
    const dimensions = pictureSize(bytes);
    if (!dimensions || Math.max(dimensions.width, dimensions.height) <= size) continue;
    const smaller = await resize(bytes, image.mimeType || "", size);
    if (!smaller) continue;
    changed.set(image.bufferView, smaller.bytes);
    image.mimeType = smaller.type;
  }
  if (!changed.size) return 0;
  // Each buffer view's bytes, in order, each at a multiple of 4.
  const parts = [];
  let length = 0;
  views.forEach((view, index) => {
    if ((view.buffer || 0) !== 0) return;
    const bytes = changed.get(index) || bin.subarray(view.byteOffset || 0, (view.byteOffset || 0) + view.byteLength);
    length += (4 - length % 4) % 4;
    parts.push([length, bytes]);
    view.byteOffset = length;
    view.byteLength = bytes.byteLength;
    length += bytes.byteLength;
  });
  const out = new Uint8Array(length);
  for (const [at, bytes] of parts) out.set(bytes, at);
  glb.bin = out;
  return changed.size;
}

// Notes where Blip had a model made, in asset.extras.blip, and who made it.
export function noteOrigin({ json }, origin, generator) {
  json.asset ||= { version: "2.0" };
  json.asset.extras = { ...(json.asset.extras || {}), blip: origin };
  if (generator) json.asset.generator = generator;
}

// The clips of other GLBs of the same model, joined to the first's: each
// channel goes to the node of the same name. Tripo can send each animation
// of a character as a file of its own. Returns { json, bin }.
export function joinClips(first, others) {
  const json = first.json;
  json.animations ||= [];
  json.accessors ||= [];
  json.bufferViews ||= [];
  const parts = [first.bin];
  let size = first.bin.byteLength;
  const byName = new Map((json.nodes || []).map((node, index) => [node.name, index]));
  for (const other of others) {
    const nodes = other.json.nodes || [];
    const copied = new Map();     // the other's accessor -> this one's
    const copy = (index) => {
      if (copied.has(index)) return copied.get(index);
      const accessor = other.json.accessors[index];
      const view = other.json.bufferViews[accessor.bufferView];
      const start = (view.byteOffset || 0) + (accessor.byteOffset || 0);
      const length = accessor.count * COMPONENTS[accessor.type] * ({ 5120: 1, 5121: 1, 5122: 2, 5123: 2, 5125: 4, 5126: 4 }[accessor.componentType] || 4);
      if (view.byteStride && view.byteStride !== length / accessor.count) throw new GlbError("An animation's keys are interleaved");
      const padding = (4 - size % 4) % 4;
      if (padding) {
        parts.push(new Uint8Array(padding));
        size += padding;
      }
      parts.push(other.bin.slice(start, start + length));
      json.bufferViews.push({ buffer: 0, byteOffset: size, byteLength: length });
      size += length;
      const { bufferView, byteOffset, sparse, ...rest } = accessor;
      json.accessors.push({ ...rest, bufferView: json.bufferViews.length - 1 });
      copied.set(index, json.accessors.length - 1);
      return json.accessors.length - 1;
    };
    for (const animation of other.json.animations || []) {
      const samplers = [], channels = [];
      const samplerFor = new Map();
      for (const channel of animation.channels || []) {
        const target = channel.target || {};
        const node = byName.get(nodes[target.node] && nodes[target.node].name);
        if (node === undefined) continue;
        if (!samplerFor.has(channel.sampler)) {
          const sampler = animation.samplers[channel.sampler];
          samplers.push({ ...sampler, input: copy(sampler.input), output: copy(sampler.output) });
          samplerFor.set(channel.sampler, samplers.length - 1);
        }
        channels.push({ ...channel, sampler: samplerFor.get(channel.sampler), target: { ...target, node } });
      }
      if (channels.length) json.animations.push({ ...animation, samplers, channels });
    }
  }
  const bin = new Uint8Array(size);
  let at = 0;
  for (const part of parts) {
    bin.set(part, at);
    at += part.byteLength;
  }
  return { json, bin };
}
